"use client";

/**
 * VenueComponent
 * Venue information section for landing pages
 */

import { LandingPageComponent, VenueConfig } from "@/lib/database/landing-pages";
import { DEFAULT_VALUES } from "../../../_logic/constants";
import { generateColorPalette, DEFAULT_PRIMARY_COLOR } from "../../../_logic/color-utils";
import { MapPin, ExternalLink, ChevronLeft, ChevronRight } from "lucide-react";
import { useState, useCallback, useEffect, useMemo } from "react";

interface VenueComponentProps {
  component: LandingPageComponent;
  eventId: number;
  primaryColor?: string;
}

export default function VenueComponent({ component, eventId, primaryColor }: VenueComponentProps) {
  // Config
  const config = (component.config || {}) as VenueConfig;

  // Color palette
  const palette = useMemo(() => generateColorPalette(primaryColor || DEFAULT_PRIMARY_COLOR), [primaryColor]);

  // Constants
  const venueName = config.venueName || DEFAULT_VALUES.VENUE_NAME;
  const address = config.address || "";
  const description = config.description || "";
  const websiteUrl = config.websiteUrl || "";
  const venueImages = config.venueImages || [];
  const hasMultipleImages = venueImages.length > 1;

  // States
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalImageIndex, setModalImageIndex] = useState(0);
  const [zoomLevel, setZoomLevel] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // Effects
  // Reset carousel slide if out of bounds
  useEffect(() => {
    if (currentSlide >= venueImages.length && venueImages.length > 0) {
      setCurrentSlide(0);
    }
  }, [venueImages.length, currentSlide]);

  // Handlers
  const nextSlide = useCallback(() => {
    if (venueImages.length > 0) {
      setCurrentSlide((prev) => (prev + 1) % venueImages.length);
    }
  }, [venueImages.length]);

  const prevSlide = useCallback(() => {
    if (venueImages.length > 0) {
      setCurrentSlide((prev) => (prev - 1 + venueImages.length) % venueImages.length);
    }
  }, [venueImages.length]);

  const goToSlide = useCallback((index: number) => {
    setCurrentSlide(index);
  }, []);

  const openModal = useCallback((index: number) => {
    setModalImageIndex(index);
    setIsModalOpen(true);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const closeModal = useCallback(() => {
    setIsModalOpen(false);
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, []);

  const zoomIn = useCallback(() => {
    setZoomLevel((prev) => Math.min(prev + 0.5, 3));
  }, []);

  const zoomOut = useCallback(() => {
    setZoomLevel((prev) => Math.max(prev - 0.5, 0.5));
  }, []);

  const goToModalPrevious = useCallback(() => {
    setModalImageIndex((prev) => (prev === 0 ? venueImages.length - 1 : prev - 1));
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [venueImages.length]);

  const goToModalNext = useCallback(() => {
    setModalImageIndex((prev) => (prev === venueImages.length - 1 ? 0 : prev + 1));
    setZoomLevel(1);
    setPosition({ x: 0, y: 0 });
  }, [venueImages.length]);

  // Pan/drag handlers
  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (zoomLevel > 1) {
        setIsDragging(true);
        setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
      }
    },
    [zoomLevel, position]
  );

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (isDragging && zoomLevel > 1) {
        const newX = e.clientX - dragStart.x;
        const newY = e.clientY - dragStart.y;

        // Calculate bounds to prevent dragging to empty space
        const container = e.currentTarget as HTMLElement;
        const maxX = (container.offsetWidth * (zoomLevel - 1)) / 2;
        const maxY = (container.offsetHeight * (zoomLevel - 1)) / 2;

        setPosition({
          x: Math.max(-maxX, Math.min(maxX, newX)),
          y: Math.max(-maxY, Math.min(maxY, newY)),
        });
      }
    },
    [isDragging, dragStart, zoomLevel]
  );

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Button hover handlers
  const handleButtonMouseEnter = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.currentTarget.style.backgroundColor = palette.buttonHover;
    },
    [palette.buttonHover]
  );

  const handleButtonMouseLeave = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.currentTarget.style.backgroundColor = palette.primary;
    },
    [palette.primary]
  );

  return (
    <div id="venue" className="bg-white py-16">
      <div className="max-w-7xl mx-auto px-4">
        <div className="grid md:grid-cols-2 gap-8 lg:gap-12 items-start">
          {/* Left Side - Venue Info */}
          <div className="space-y-6 md:pt-8">
            <h3 className="text-2xl font-bold text-gray-900">{venueName}</h3>

            {address && (
              <div className="flex items-start gap-3">
                <MapPin className="w-5 h-5 mt-0.5 shrink-0" style={{ color: palette.primary }} />
                <p className="text-gray-600">{address}</p>
              </div>
            )}

            {description && <p className="text-gray-600 leading-relaxed whitespace-pre-line">{description}</p>}

            {websiteUrl && (
              <a
                href={websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-6 py-3 rounded-lg transition-colors font-medium text-white"
                style={{
                  backgroundColor: palette.primary,
                }}
                onMouseEnter={handleButtonMouseEnter}
                onMouseLeave={handleButtonMouseLeave}
              >
                {config.customButtonText || "Visit Hotel Website"}
                <ExternalLink className="w-4 h-4" />
              </a>
            )}
          </div>

          {/* Right Side - Image/Carousel */}
          <div className="relative h-[400px] md:h-[500px] rounded-2xl overflow-hidden shadow-md bg-gray-100">
            {venueImages.length > 0 ? (
              <>
                {/* Images */}
                {venueImages.map((imageUrl, index) => (
                  <button
                    key={index}
                    type="button"
                    onClick={() => openModal(index)}
                    className={`absolute inset-0 w-full h-full transition-opacity duration-500 touch-manipulation ${
                      index === currentSlide ? "opacity-100 cursor-pointer" : "opacity-0 pointer-events-none"
                    }`}
                    aria-label={`View ${venueName} image ${index + 1}`}
                  >
                    <img
                      src={imageUrl}
                      alt={`${venueName} ${index + 1}`}
                      className="w-full h-full object-cover hover:opacity-90 transition-opacity"
                    />
                  </button>
                ))}

                {/* Carousel Controls - Only show if multiple images */}
                {hasMultipleImages && (
                  <>
                    {/* Navigation Arrows */}
                    <button
                      type="button"
                      onClick={prevSlide}
                      className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-lg p-3 shadow-lg transition-all z-10 touch-manipulation"
                      aria-label="Previous image"
                    >
                      <ChevronLeft className="w-6 h-6 text-gray-900" />
                    </button>
                    <button
                      type="button"
                      onClick={nextSlide}
                      className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white rounded-lg p-3 shadow-lg transition-all z-10 touch-manipulation"
                      aria-label="Next image"
                    >
                      <ChevronRight className="w-6 h-6 text-gray-900" />
                    </button>

                    {/* Dot Indicators */}
                    <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex gap-2 z-10">
                      {venueImages.map((_, index) => (
                        <button
                          type="button"
                          key={index}
                          onClick={() => goToSlide(index)}
                          className={`w-2.5 h-2.5 rounded-full transition-all touch-manipulation ${
                            index === currentSlide ? "bg-white w-8" : "bg-white/50 hover:bg-white/75"
                          }`}
                          aria-label={`Go to image ${index + 1}`}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            ) : (
              <div className="w-full h-full flex items-center justify-center text-gray-400">
                <p>Venue image not configured</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Image Modal with Zoom */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          {/* Backdrop button for closing */}
          <button
            type="button"
            className="absolute inset-0 bg-black bg-opacity-90 touch-manipulation"
            onClick={closeModal}
            aria-label="Close modal"
            tabIndex={-1}
          />
          {/* Close Button */}
          <button
            type="button"
            onClick={closeModal}
            className="absolute top-4 right-4 text-white hover:text-gray-300 z-50 touch-manipulation"
            aria-label="Close modal"
          >
            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>

          {/* Zoom Controls */}
          <div className="absolute top-4 left-4 flex flex-col gap-2 z-50">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                zoomIn();
              }}
              className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-lg shadow-md touch-manipulation"
              aria-label="Zoom in"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7"
                />
              </svg>
            </button>
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                zoomOut();
              }}
              className="bg-white text-gray-900 hover:bg-gray-100 p-2 rounded-lg shadow-md touch-manipulation"
              aria-label="Zoom out"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM13 10H7"
                />
              </svg>
            </button>
            <div className="bg-white text-gray-900 px-3 py-1 rounded-lg shadow-md text-sm text-center">
              {Math.round(zoomLevel * 100)}%
            </div>
          </div>

          {/* Navigation Arrows */}
          {venueImages.length > 1 && (
            <>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToModalPrevious();
                }}
                className="absolute left-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-900 p-3 rounded-lg shadow-md z-50 touch-manipulation"
                aria-label="Previous image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  goToModalNext();
                }}
                className="absolute right-4 top-1/2 -translate-y-1/2 bg-white/80 hover:bg-white text-gray-900 p-3 rounded-lg shadow-md z-50 touch-manipulation"
                aria-label="Next image"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </>
          )}

          {/* Image */}
          <div
            className="overflow-hidden max-w-full max-h-full flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            style={{ cursor: zoomLevel > 1 ? (isDragging ? "grabbing" : "grab") : "default" }}
          >
            <img
              src={venueImages[modalImageIndex]}
              alt={`${venueName} ${modalImageIndex + 1}`}
              className="max-w-none transition-transform duration-200 select-none"
              draggable={false}
              style={{
                transform: `scale(${zoomLevel}) translate(${position.x / zoomLevel}px, ${position.y / zoomLevel}px)`,
                transformOrigin: "center center",
              }}
            />
          </div>

          {/* Image Counter */}
          {venueImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/50 text-white px-4 py-2 rounded-lg">
              {modalImageIndex + 1} / {venueImages.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
